import { FavoritesToggleButton } from '@src/components/StrategySelector/FavoritesToggleButton';
import { FavoriteToggleIcon } from '@src/components/StrategySelector/FavoriteToggleIcon';
import { SubAccountSearchSelect, useSubAccountSearchSelectOptions } from '@src/components/SubAccounts';
import { isEligibleTransferFilterSubAccount } from '@src/containers/Transfers/subAccountFilterUtils';
import {
  buildOptionsWithRecentsGroup,
  sourceDestinationEqualityChecker,
} from '@src/containers/Transfers/transferFormPreferenceUtils';
import {
  formatSourceDestinationDescription,
  getAccountAvailableBalance,
  getTransferBalanceDescription,
} from '@src/containers/Transfers/transferOptionUtils';
import {
  errorWithHelp,
  getEmptyTransferForm,
  shouldShowDestinationBalance,
  type SourceDestinationOption,
  type TransferForm,
} from '@src/containers/Transfers/types';
import { useTransferFormOptions } from '@src/containers/Transfers/useTransferFormOptions';
import { useTransferFormValidation } from '@src/containers/Transfers/useTransferFormValidation';
import { useTransferPreferences } from '@src/containers/Transfers/useTransferPreferences';
import { useBalances } from '@src/hooks/useBalances';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { getPermittedMarketAccountsSet } from '@src/hooks/usePermittedMarketAccountsSet';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useTransfers } from '@src/providers';
import { useMarketAccountPermissionFilters } from '@src/providers/MarketAccountPermissionFiltersProvider';
import type { BalancesFilter } from '@src/types';
import { getRestFailureUserMessage } from '@src/utils/rtkQuery/restFailureError';
import {
  ACTION,
  AutocompleteResult,
  Box,
  Button,
  ButtonVariants,
  Dialog,
  Divider,
  Flex,
  FormControlSizes,
  FormGroup,
  FormMessage,
  Grid,
  HStack,
  HelpIcon,
  Icon,
  IconName,
  InlineFormattedNumber,
  Input,
  MixpanelEvent,
  NotificationVariants,
  NumberInput,
  SearchSelect,
  Stack,
  Text,
  format,
  getTypedKeys,
  logger,
  toBig,
  useCurrency,
  useDisclosure,
  useGlobalToasts,
  useMarketAccountsContext,
  useMixpanel,
  useObservableValue,
  type ISubaccount,
  type Market,
  type MarketAccount,
  type RenderResultFunc,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { AdminMACHelpTooltip } from './AdminMACHelpTooltip';
import { CenteredRow, DialogWrapper, InfoWrapper, TransferFormContainer, TransferFormWrapper } from './styles';
import { ANIMATION_DURATION } from './TransferConfirmation/styles';
import {
  TransferConfirmationOverlay,
  type PendingTransferConfirmation,
} from './TransferConfirmation/TransferConfirmationOverlay';
import { TransferFormHomeCurrencyEstimate } from './TransferFormHomeCurrencyEstimate';

const getProviderLabel = (providerOption: Market) => providerOption.DisplayName;
const getAssetLabel = (asset: string) => asset;
const getSourceDestinationLabel = (o: SourceDestinationOption) => o.label;
const getSourceDestinationDescription = (o: SourceDestinationOption) => o.description ?? '';

const ACCOUNT_DROPDOWN_HEIGHT = 400;
const FIREBLOCKS = 'fireblocks';
const validationFieldKeys = ['provider', 'asset', 'source', 'destination', 'amount', 'chainCurrency'] as const;
const allTouchedFields = Object.fromEntries(validationFieldKeys.map(field => [field, true] as const));

const matchesSourceDestinationAddress = (item: SourceDestinationOption, searchString: string) =>
  item.addressForCurrency?.toLowerCase().includes(searchString) ?? false;

type TransferFormFieldKey = (typeof validationFieldKeys)[number];
type TransferFormTouchedFields = Partial<Record<TransferFormFieldKey, boolean>>;

interface TransferFormComponentProps {
  initialForm?: TransferForm;
  scrollLogs?: boolean;
  portalizeDropdowns?: boolean;
}

function calculateInitialForm(
  initialForm: TransferForm | undefined,
  marketAccountsByID: Map<number, MarketAccount>
): TransferForm {
  if (initialForm == null) {
    return getEmptyTransferForm();
  }

  const definedSourceOrDestination = initialForm.source ?? initialForm.destination;
  if (definedSourceOrDestination != null && !initialForm.provider) {
    // There is a specified source or destination but there is no provider. Apply a provider.
    const providerName = marketAccountsByID.get(definedSourceOrDestination.marketAccount.MarketAccountID)?.Market;
    return providerName ? { ...initialForm, provider: providerName } : getEmptyTransferForm();
  }

  return initialForm;
}

export function TransferFormDrawer({
  initialForm,
  scrollLogs = false,
  portalizeDropdowns = true,
}: TransferFormComponentProps) {
  const { marketAccountsByID, marketAccountsList } = useMarketAccountsContext();
  const { initiateTransfer } = useTransfers();
  const mixpanel = useMixpanel();
  const { add: addToast } = useGlobalToasts();
  const { enableAccountSegregation, enableAccountSegregationConfiguration, enableTransferSubAccountFilter } =
    useFeatureFlag();
  const enableTransferSubAccountFilterActive =
    (enableAccountSegregation || enableAccountSegregationConfiguration) && enableTransferSubAccountFilter;
  const { marketAccountFilterIDsBySubAccountName, marketAccountPermissionFiltersByFilterID } =
    useMarketAccountPermissionFilters();

  const [transferForm, setTransferForm] = useState(calculateInitialForm(initialForm, marketAccountsByID));
  const [subAccount, setSubAccount] = useState<ISubaccount | undefined>(undefined);
  const [touchedFields, setTouchedFields] = useState<TransferFormTouchedFields>({});
  const [isLoading, setIsLoading] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingTransferConfirmation | null>(null);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

  const { isAuthorized } = useRoleAuth();
  const authorizedForTransfer = isAuthorized(ACTION.SUBMIT_TRANSFER);

  const { provider, amount, asset, chainCurrency, source, destination, reference, description, treatAsGrossAmount } =
    transferForm;

  const allSubAccountOptions = useSubAccountSearchSelectOptions();
  // Eligible filters need Write access to at least one active market account.
  const eligibleSubAccountOptions = useMemo(
    () =>
      allSubAccountOptions.filter(option =>
        isEligibleTransferFilterSubAccount(option.Name, {
          enableAccountSegregation: enableTransferSubAccountFilterActive,
          marketAccountsList,
          marketAccountFilterIDsBySubAccountName,
          marketAccountPermissionFiltersByFilterID,
        })
      ),
    [
      allSubAccountOptions,
      enableTransferSubAccountFilterActive,
      marketAccountsList,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,
    ]
  );
  const selectedSubAccountIsEligible =
    subAccount == null || eligibleSubAccountOptions.some(option => option.Name === subAccount.Name);
  const showSubAccountFilter = enableTransferSubAccountFilterActive && eligibleSubAccountOptions.length > 0;

  // Under account segregation, an eligible selected sub account narrows selectable source market accounts to its mappings.
  // `null` means no restriction, including while an ineligible selection is being cleared.
  const permittedSourceMarketAccountNames = useMemo(
    () =>
      getPermittedMarketAccountsSet({
        subAccountAllocations:
          subAccount?.Name && selectedSubAccountIsEligible ? [{ subAccount: subAccount.Name, value: '1' }] : -1,
        enableAccountSegregation: enableTransferSubAccountFilterActive,
        marketAccountsList,
        marketAccountFilterIDsBySubAccountName,
        marketAccountPermissionFiltersByFilterID,
      }),
    [
      subAccount?.Name,
      selectedSubAccountIsEligible,
      enableTransferSubAccountFilterActive,
      marketAccountsList,
      marketAccountFilterIDsBySubAccountName,
      marketAccountPermissionFiltersByFilterID,
    ]
  );

  const { providerOptions, assetOptions, chainCurrencyOptions, sourceOptions, destinationOptions } =
    useTransferFormOptions(transferForm, permittedSourceMarketAccountNames);

  // Clear the selected sub account if it is no longer eligible (e.g. mappings changed or account segregation turned off).
  useEffect(() => {
    if (!selectedSubAccountIsEligible) {
      setSubAccount(undefined);
    }
  }, [selectedSubAccountIsEligible]);

  const {
    sortedAssetOptions,
    showOnlyFavorites,
    setShowOnlyFavorites,
    favoriteTransferAssetsSet,
    toggleFavoriteTransferAsset,
    getRecentSourceAccountNames,
    getRecentDestinationAccountNames,
    recordRecentSource,
    recordRecentDestination,
    maxRecentTransferAccounts,
  } = useTransferPreferences(assetOptions);

  const recentSourceAccountNames = getRecentSourceAccountNames(provider, asset);
  const recentDestinationAccountNames = getRecentDestinationAccountNames(provider, asset);

  const { optionsWithRecents: sourceOptionsWithRecents, getGroup: getSourceGroup } = useMemo(
    () => buildOptionsWithRecentsGroup(sourceOptions, recentSourceAccountNames, maxRecentTransferAccounts),
    [sourceOptions, recentSourceAccountNames, maxRecentTransferAccounts]
  );

  const { optionsWithRecents: destinationOptionsWithRecents, getGroup: getDestinationGroup } = useMemo(
    () => buildOptionsWithRecentsGroup(destinationOptions, recentDestinationAccountNames, maxRecentTransferAccounts),
    [destinationOptions, recentDestinationAccountNames, maxRecentTransferAccounts]
  );

  const currencyInfo = useCurrency(asset);
  const { DefaultIncrement } = currencyInfo ?? {};

  const updateTransfer = useCallback((update: Partial<TransferForm>) => {
    setTransferForm(prev => ({ ...prev, ...update }));
  }, []);

  const touchField = useCallback((field: TransferFormFieldKey) => {
    setTouchedFields(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const touchAllFields = useCallback(() => {
    setTouchedFields(allTouchedFields);
  }, []);

  const resetTouchedFields = useCallback((fields?: readonly TransferFormFieldKey[]) => {
    if (fields == null) {
      setTouchedFields({});
      return;
    }

    setTouchedFields(prev => {
      const next = { ...prev };
      fields.forEach(field => {
        delete next[field];
      });
      return next;
    });
  }, []);

  // Clear source + chainCurrency when the selected source is no longer permitted.
  // Untouch those fields so the app-driven clear doesn't flash "required" errors.
  useEffect(() => {
    if (permittedSourceMarketAccountNames == null) {
      return;
    }

    const sourceInvalid = source != null && !permittedSourceMarketAccountNames.has(source.marketAccount.Name);
    if (!sourceInvalid) {
      return;
    }

    resetTouchedFields(['chainCurrency', 'source']);
    setTransferForm(prev => ({
      ...prev,
      source: undefined,
      chainCurrency: '',
    }));
  }, [permittedSourceMarketAccountNames, source, resetTouchedFields]);

  const handleDismissConfirmation = useCallback(() => {
    setIsConfirmationVisible(false);
    const dismissingClientID = pendingConfirmation?.clientID;
    window.setTimeout(() => {
      setPendingConfirmation(prev => (prev?.clientID === dismissingClientID ? null : prev));
    }, ANIMATION_DURATION);
  }, [pendingConfirmation?.clientID]);

  const handleClear = useCallback(() => {
    handleDismissConfirmation();
    updateTransfer(getEmptyTransferForm());
    setSubAccount(undefined);
    resetTouchedFields();
  }, [handleDismissConfirmation, resetTouchedFields, updateTransfer]);

  const handleSubAccountChange = useCallback(
    (next: ISubaccount | undefined) => {
      setSubAccount(next);
      if (next != null) {
        mixpanel.track(MixpanelEvent.SelectSubAccount);
      }
    },
    [mixpanel]
  );

  // Currency-scoped only: MarketAccounts would omit balances for unselected dropdown options,
  // causing false zero amounts in option descriptions while the list is open.
  const balancesFilter: BalancesFilter = useMemo(
    () => ({
      Currencies: compact([asset]),
    }),
    [asset]
  );

  const { balancesByCurrencyMarketAccountIDObs } = useBalances({
    filter: balancesFilter,
    tag: 'TransferFormComponent',
    openStreamWithoutFilter: false,
  });
  const balancesByCurrencyMarketAccountID = useObservableValue(
    () => balancesByCurrencyMarketAccountIDObs,
    [balancesByCurrencyMarketAccountIDObs]
  );

  // If destination changed, see if reference data exists to populate the Reference
  // If there is no reference data, clear out reference and description
  useEffect(() => {
    if (destination?.marketAccount?.ReferenceData && provider !== 'fireblocks') {
      updateTransfer({ reference: destination.marketAccount.ReferenceData, description: '' });
    } else {
      updateTransfer({ reference: '', description: '' });
    }
  }, [source, destination, marketAccountsByID, provider, updateTransfer]);

  const { availableDestinationAmount, availableSourceAmount } = useMemo(() => {
    const sourceAvailableBalance = source
      ? getAccountAvailableBalance(
          source.marketAccount.MarketAccountID,
          asset || undefined,
          balancesByCurrencyMarketAccountID
        )
      : undefined;
    const destinationAvailableBalance =
      destination?.marketAccount != null && shouldShowDestinationBalance(destination.marketAccount)
        ? getAccountAvailableBalance(
            destination.marketAccount.MarketAccountID,
            asset || undefined,
            balancesByCurrencyMarketAccountID
          )
        : undefined;

    return {
      availableSourceAmount: sourceAvailableBalance?.availableAmount,
      availableDestinationAmount: destinationAvailableBalance?.availableAmount,
    };
  }, [asset, balancesByCurrencyMarketAccountID, destination, source]);

  const getAvailableBalanceDescription = useCallback(
    (marketAccountId: number, destinationMarketAccount?: MarketAccount) =>
      getTransferBalanceDescription(
        marketAccountId,
        asset || undefined,
        balancesByCurrencyMarketAccountID,
        DefaultIncrement,
        destinationMarketAccount
      ),
    [asset, balancesByCurrencyMarketAccountID, DefaultIncrement]
  );

  const getSourceOptionDescription = useCallback(
    (option: SourceDestinationOption) =>
      formatSourceDestinationDescription(
        option.description,
        getAvailableBalanceDescription(option.marketAccount.MarketAccountID)
      ) ?? '',
    [getAvailableBalanceDescription]
  );

  const getDestinationOptionDescription = useCallback(
    (option: SourceDestinationOption) =>
      formatSourceDestinationDescription(
        option.description,
        getAvailableBalanceDescription(option.marketAccount.MarketAccountID, option.marketAccount)
      ) ?? '',
    [getAvailableBalanceDescription]
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement> | React.MouseEvent) => {
      e?.preventDefault();
      if (!source?.marketAccount || !destination?.marketAccount) {
        logger.error(new Error(`Unknown Source Account Info or Destination Account Info for ${provider} Transfer`));
        return;
      }
      const newClientID = uuid();
      setIsLoading(true);
      mixpanel.track(MixpanelEvent.InitiatePrincipalTransfer);
      try {
        const initiateTransferResponse = await initiateTransfer({
          clientID: newClientID,
          currency: asset,
          chainCurrency: chainCurrency || undefined,
          amount,
          market: provider,
          fromMarketAccountID: source.marketAccount.MarketAccountID,
          toMarketAccountID: destination.marketAccount.MarketAccountID,
          fromSourceAccountID: source.marketAccount.SourceAccountID,
          toSourceAccountID: destination.marketAccount.SourceAccountID,
          description,
          reference,
          treatAsGrossAmount,
        });
        recordRecentSource(provider, asset, source.marketAccount.Name);
        recordRecentDestination(provider, asset, destination.marketAccount.Name);
        setPendingConfirmation({
          clientID: newClientID,
          transferID: initiateTransferResponse.transferID,
          seedTransfer: initiateTransferResponse.transfer,
        });
        setIsConfirmationVisible(true);
      } catch (error) {
        addToast({
          text: getRestFailureUserMessage(error) ?? 'Failed to submit transfer',
          variant: NotificationVariants.Negative,
          dismissable: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      mixpanel,
      initiateTransfer,
      addToast,
      asset,
      chainCurrency,
      amount,
      provider,
      source,
      destination,
      description,
      reference,
      treatAsGrossAmount,
      recordRecentSource,
      recordRecentDestination,
    ]
  );

  const confirmTransferDialog = useDisclosure();

  const allInputsDisabled = !authorizedForTransfer || isLoading;

  const { errors, disabledInputs } = useTransferFormValidation(
    transferForm,
    allInputsDisabled,
    chainCurrencyOptions,
    availableSourceAmount,
    balancesByCurrencyMarketAccountID
  );

  const theme = useTheme();
  const hasErrors = Object.keys(errors).length > 0;
  const allFieldsTouched = validationFieldKeys.every(field => touchedFields[field]);
  const visibleErrorKeys = getTypedKeys(errors).filter((error): error is TransferFormFieldKey =>
    validationFieldKeys.includes(error as TransferFormFieldKey)
  );
  const submitErrorMessages = compact([errors.balances, ...visibleErrorKeys.map(error => errors[error])]);
  const getFieldError = useCallback(
    (field: TransferFormFieldKey) => (touchedFields[field] ? errors?.[field] : undefined),
    [errors, touchedFields]
  );

  const handleAssetChange = useCallback(
    (asset: string | undefined) => {
      touchField('asset');
      resetTouchedFields(['source', 'destination', 'chainCurrency']);
      // Reset source, destination and chainCurrency selections on asset change
      updateTransfer({ asset, source: undefined, destination: undefined, chainCurrency: '' });
    },
    [resetTouchedFields, touchField, updateTransfer]
  );

  const handleProviderChange = useCallback(
    (po: Market | undefined) => {
      touchField('provider');
      resetTouchedFields(['asset', 'amount', 'source', 'destination', 'chainCurrency']);
      // Reset a bunch of stuff on provider change
      updateTransfer({
        provider: po?.Name,
        asset: '',
        amount: '',
        source: undefined,
        destination: undefined,
        reference: '',
        chainCurrency: '',
        treatAsGrossAmount: false,
      });
    },
    [resetTouchedFields, touchField, updateTransfer]
  );

  const handleSourceChange = useCallback(
    (newSource: SourceDestinationOption | undefined) => {
      resetTouchedFields(['chainCurrency']);
      setTransferForm(prev => {
        return {
          ...prev,
          source: newSource,
          chainCurrency: '',
        };
      });
      touchField('source');
    },
    [resetTouchedFields, touchField]
  );

  const handleDestinationChange = useCallback(
    (newDestination: SourceDestinationOption | undefined) => {
      resetTouchedFields(['chainCurrency']);
      setTransferForm(prev => {
        return {
          ...prev,
          destination: newDestination,
          chainCurrency: '',
        };
      });
      touchField('destination');
    },
    [resetTouchedFields, touchField]
  );

  const providerSelection = useMemo(
    () => providerOptions.find((po: Market) => po.Name === provider),
    [provider, providerOptions]
  );

  const disableChainSelection = chainCurrencyOptions.required === false || source == null || destination == null;
  const maxAvailableSet = areStringAmountsEqual(transferForm.amount, availableSourceAmount);
  const isFireblocks = transferForm.provider === FIREBLOCKS;
  const sourceFieldError = getFieldError('source');
  const destinationFieldError = getFieldError('destination');

  const renderAssetFavoriteIcon = useCallback(
    (assetOption: string) => (
      <FavoriteToggleIcon
        isFavorite={favoriteTransferAssetsSet.has(assetOption)}
        onToggle={() => toggleFavoriteTransferAsset(assetOption)}
        data-testid={`transfer-asset-favorite-${assetOption}`}
      />
    ),
    [favoriteTransferAssetsSet, toggleFavoriteTransferAsset]
  );

  const renderAssetResult: RenderResultFunc<string> = useCallback(
    (searchResults, disabled, searchString) =>
      AutocompleteResult(searchResults, disabled, searchString, renderAssetFavoriteIcon),
    [renderAssetFavoriteIcon]
  );

  const assetSearchPrefix = useMemo(
    () => (
      <FavoritesToggleButton isActive={showOnlyFavorites} onToggle={() => setShowOnlyFavorites(!showOnlyFavorites)} />
    ),
    [showOnlyFavorites, setShowOnlyFavorites]
  );

  return (
    <TransferFormContainer>
      <TransferFormWrapper as="form" onSubmit={handleSubmit} gap="spacing4">
        <Flex gap="spacing12">
          <FormGroup
            label="Provider"
            required
            disabled={disabledInputs.provider}
            flex="1"
            error={getFieldError('provider')}
          >
            <SearchSelect
              disabled={disabledInputs.provider}
              selection={providerSelection}
              options={providerOptions}
              onChange={handleProviderChange}
              getLabel={getProviderLabel}
              portalize={portalizeDropdowns}
              data-testid="providers-select"
              invalid={getFieldError('provider') != null}
              touched={touchedFields.provider}
            />
          </FormGroup>
          <FormGroup label="Asset" required disabled={disabledInputs.asset} flex="1" error={getFieldError('asset')}>
            <SearchSelect
              disabled={disabledInputs.asset}
              selection={asset}
              options={sortedAssetOptions}
              onChange={handleAssetChange}
              getLabel={getAssetLabel}
              portalize={portalizeDropdowns}
              data-testid="asset-select"
              invalid={getFieldError('asset') != null}
              touched={touchedFields.asset}
              initialSortByLabel={false}
              searchPrefix={assetSearchPrefix}
              renderResult={renderAssetResult}
            />
          </FormGroup>
        </Flex>
        {showSubAccountFilter && (
          <FormGroup
            label="Sub Account"
            tooltip="Narrows Source to accounts permitted for the selected Sub Account."
            mb={0}
            disabled={allInputsDisabled}
          >
            <SubAccountSearchSelect
              selection={subAccount}
              options={eligibleSubAccountOptions}
              onChange={handleSubAccountChange}
              disabled={allInputsDisabled}
              portalize={portalizeDropdowns}
              // Override the component's default fixed width so the dropdown matches this full-width control.
              dropdownWidth={undefined}
              data-testid="transfer-sub-account-select"
            />
          </FormGroup>
        )}
        <FormGroup
          label={
            <AccountFormGroupLabel label="Source*" account={source?.marketAccount.MarketAccountID} asset={asset} />
          }
          disabled={disabledInputs.source}
          {...errorWithHelp(
            sourceFieldError,
            availableSourceAmount ? (
              <>
                Available Balance:{' '}
                {sourceFieldError ? (
                  <InlineFormattedNumber
                    number={availableSourceAmount}
                    currency={asset}
                    increment={DefaultIncrement}
                    highlightColor="inherit"
                    data-testid="available-balance-amount-text"
                  />
                ) : (
                  <Text as="span" color="colorTextImportant" data-testid="available-balance-amount-text">
                    <InlineFormattedNumber
                      number={availableSourceAmount}
                      currency={asset}
                      increment={DefaultIncrement}
                    />
                  </Text>
                )}
              </>
            ) : undefined
          )}
        >
          <SearchSelect
            selection={source}
            onChange={handleSourceChange}
            options={sourceOptionsWithRecents}
            getLabel={getSourceDestinationLabel}
            getDescription={getSourceOptionDescription}
            getButtonDescription={getSourceDestinationDescription}
            disabled={disabledInputs.source}
            touched={touchedFields.source}
            invalid={sourceFieldError != null}
            showDescriptionInButton
            getGroup={getSourceGroup}
            equalityChecker={sourceDestinationEqualityChecker}
            initialSortByLabel={false}
            maxHeight={ACCOUNT_DROPDOWN_HEIGHT}
            portalize={portalizeDropdowns}
            additionalSearchMatcher={matchesSourceDestinationAddress}
            showClear
            data-testid="source-select"
          />
        </FormGroup>
        <FormGroup
          label={
            <AccountFormGroupLabel
              label="Destination*"
              account={destination?.marketAccount.MarketAccountID}
              asset={asset}
            />
          }
          disabled={disabledInputs.destination}
          {...errorWithHelp(
            destinationFieldError,
            availableDestinationAmount ? (
              <>
                Available Balance:{' '}
                {destinationFieldError ? (
                  <InlineFormattedNumber
                    number={availableDestinationAmount}
                    currency={asset}
                    increment={DefaultIncrement}
                    highlightColor="inherit"
                  />
                ) : (
                  <Text as="span" color="colorTextImportant">
                    <InlineFormattedNumber
                      number={availableDestinationAmount}
                      currency={asset}
                      increment={DefaultIncrement}
                    />
                  </Text>
                )}
              </>
            ) : undefined
          )}
        >
          <SearchSelect
            selection={destination}
            onChange={handleDestinationChange}
            options={destinationOptionsWithRecents}
            getLabel={getSourceDestinationLabel}
            getDescription={getDestinationOptionDescription}
            getButtonDescription={getSourceDestinationDescription}
            disabled={disabledInputs.destination}
            touched={touchedFields.destination}
            invalid={destinationFieldError != null}
            getGroup={getDestinationGroup}
            equalityChecker={sourceDestinationEqualityChecker}
            initialSortByLabel={false}
            showDescriptionInButton
            maxHeight={ACCOUNT_DROPDOWN_HEIGHT}
            portalize={portalizeDropdowns}
            additionalSearchMatcher={matchesSourceDestinationAddress}
            showClear
            data-testid="destination-select"
          />
        </FormGroup>
        <Stack gap="spacing8">
          <FormGroup
            label="Amount"
            required
            disabled={disabledInputs.amount}
            error={getFieldError('amount')}
            labelSuffix={
              <HStack gap="spacing4">
                <Button
                  data-testid="max-available-button"
                  size={FormControlSizes.Xxs}
                  dim={maxAvailableSet}
                  disabled={disabledInputs.amount || !availableSourceAmount}
                  onClick={() => {
                    touchField('amount');
                    updateTransfer({ amount: availableSourceAmount, treatAsGrossAmount: isFireblocks });
                  }}
                >
                  Max Available
                </Button>
                {/* Fireblocks may deduct gas fees from Max Available for base assets. */}
                {isFireblocks && (
                  <HelpIcon tooltip="If the asset is a base asset (e.g., ETH, MATIC), selecting Max Amount will deduct the transaction gas fee from the requested amount." />
                )}
              </HStack>
            }
          >
            <NumberInput
              disabled={disabledInputs.amount}
              touched={touchedFields.amount}
              invalid={getFieldError('amount') != null}
              name="amount"
              onChange={value => {
                touchField('amount');
                updateTransfer({ amount: value, treatAsGrossAmount: false });
              }}
              onBlur={() => touchField('amount')}
              autoComplete="off"
              min="0"
              value={amount}
              suffix={asset}
              data-testid="amount-input"
            />
          </FormGroup>
          <TransferFormHomeCurrencyEstimate amount={amount} asset={asset} />
        </Stack>
        <Flex gap="spacing12">
          <FormGroup label="Chain" flex="1" error={getFieldError('chainCurrency')}>
            <SearchSelect
              selection={chainCurrency}
              options={chainCurrencyOptions.chainOptions}
              onChange={chainCurrency => {
                updateTransfer({ chainCurrency });
                touchField('chainCurrency');
              }}
              getLabel={chainCurrency => chainCurrency}
              portalize={portalizeDropdowns}
              disabled={disableChainSelection}
              touched={touchedFields.chainCurrency}
              invalid={getFieldError('chainCurrency') != null}
              onBlur={() => touchField('chainCurrency')}
              showClear
              data-testid="chain-select"
            />
          </FormGroup>
          <FormGroup label="Reference (Provider)" disabled={disabledInputs.reference} flex="1">
            <Input
              name="reference"
              onChange={e => updateTransfer({ reference: e.target.value })}
              autoComplete="off"
              value={reference}
              placeholder={provider === 'fireblocks' ? 'Disabled for Fireblocks' : 'Optional'}
              disabled={disabledInputs.reference}
            />
          </FormGroup>
        </Flex>
        <Divider mb="spacing16" />
        <FormGroup label="Internal Comment" disabled={disabledInputs.description} mb={0}>
          <Input
            name="description"
            onChange={e => updateTransfer({ description: e.target.value })}
            autoComplete="off"
            value={description}
            placeholder="Optional"
            disabled={disabledInputs.description}
          />
        </FormGroup>
      </TransferFormWrapper>
      <Stack mt="auto">
        {(submitErrorMessages.length > 0 || !authorizedForTransfer) && (
          <Box
            overflowY={scrollLogs ? 'scroll' : 'visible'}
            h={scrollLogs ? '60px' : 'auto'}
            px="spacing16"
            py="spacing8"
          >
            {!authorizedForTransfer ? (
              <InfoWrapper>
                <Icon icon={IconName.ExclamationCircle} color={theme.colors.yellow.lighten} />
                <Text textAlign="left">You are not authorized to submit transfers.</Text>
              </InfoWrapper>
            ) : (
              allFieldsTouched &&
              submitErrorMessages.length > 0 && (
                <div data-testid="error-logs">
                  {submitErrorMessages.map((error, index) => (
                    <FormMessage key={`${error}${index}`}>{error}</FormMessage>
                  ))}
                </div>
              )
            )}
          </Box>
        )}
        <Grid
          columns="1fr 2fr"
          gap="spacing16"
          alignItems="flex-start"
          px="spacing16"
          py="spacing8"
          borderTop="1px solid"
          borderColor="backgroundBody"
        >
          <Button disabled={allInputsDisabled} onClick={handleClear}>
            Clear
          </Button>
          <Flex
            flex="1"
            onMouseEnter={() => {
              if (!allInputsDisabled) {
                touchAllFields();
              }
            }}
          >
            <Button
              width="100%"
              variant={ButtonVariants.Primary}
              disabled={allInputsDisabled || hasErrors}
              onClick={() => confirmTransferDialog.open()}
              data-testid="transfer-button"
              loading={isLoading}
            >
              Transfer
            </Button>
          </Flex>
        </Grid>
      </Stack>
      <Dialog
        {...confirmTransferDialog}
        usePortal={false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleSubmit}
        title="Confirm Transfer"
      >
        <DialogWrapper>
          <div>
            Send{' '}
            <Text color="colorTextImportant">
              {format(amount, {
                spec: DefaultIncrement,
              })}{' '}
              {asset}
            </Text>{' '}
            via{' '}
            <Text color="colorTextImportant">
              {providerOptions?.find(po => po.Name === provider)?.DisplayName ?? ''}
            </Text>
          </div>
          <CenteredRow>
            <Text color="colorTextImportant">{source?.label ?? ''}</Text> <Icon icon={IconName.ArrowRight} size={22} />{' '}
            <Text color="colorTextImportant">{destination?.label ?? ''}</Text>
          </CenteredRow>
        </DialogWrapper>
      </Dialog>
      {pendingConfirmation != null && (
        <TransferConfirmationOverlay
          key={pendingConfirmation.clientID}
          pendingConfirmation={pendingConfirmation}
          isVisible={isConfirmationVisible}
          onClose={handleClear}
          onResubmit={handleDismissConfirmation}
        />
      )}
    </TransferFormContainer>
  );
}

interface AccountFormGroupLabelProps {
  label: string;
  account?: number;
  asset?: string;
}
function AccountFormGroupLabel({ label, account, asset }: AccountFormGroupLabelProps) {
  return (
    <HStack gap="spacing8" justifyContent="space-between">
      <Text>{label}</Text>
      <AdminMACHelpTooltip account={account} currency={asset} />
    </HStack>
  );
}

function areStringAmountsEqual(a: string | undefined, b: string | undefined) {
  const aBig = toBig(a);
  const bBig = toBig(b);

  // Just treat the case where either (or both!) of them are nullish as falsy, nullish == nullish is arguably not equal anyway
  if (aBig == null || bBig == null) {
    return false;
  }

  return aBig.eq(bBig);
}
